// src/client/components/profile-manager/detail-view/ProfileImageAndData.tsx
// V2.0 Multi-Platform Support

import { ExternalLink } from 'lucide-react';
import { Button } from '@client/components/ui/button';
import { Badge } from '@client/components/ui/badge';
import ParsedRawData from './ParsedRawData';
import type { ProfileWithRelations } from '@shared/types';

interface ProfileImageAndDataProps {
  profile: ProfileWithRelations;
}

/**
 * Platform Detection basierend auf vorhandenen IDs/URLs
 */
function detectPlatformLinks(profile: ProfileWithRelations): {
  hasLinkedIn: boolean;
  hasXing: boolean;
  linkedinUrl: string | null;
  xingUrl: string | null;
} {
  const profileAny = profile as any;
  
  return {
    hasLinkedIn: !!(profile.linkedinId || profile.profileUrl),
    hasXing: !!(profileAny.xingId || profileAny.xingProfileUrl),
    linkedinUrl: profile.profileUrl || null,
    xingUrl: profileAny.xingProfileUrl || null
  };
}

export default function ProfileImageAndData({
  profile,
}: ProfileImageAndDataProps) {
  const { hasLinkedIn, hasXing, linkedinUrl, xingUrl } = detectPlatformLinks(profile);

  const handleOpenProfile = (url: string, platform: 'linkedin' | 'xing') => {
    const width = 1200;
    const height = 900;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    
    window.open(
      url,
      `${platform}-profile`,
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
  };

  return (
    <div className="space-y-6">
      {/* Profile Image */}
      <div className="border rounded-lg p-6 bg-white">
        <h3 className="text-lg font-semibold mb-4">Profile Image</h3>
        
          <div className="flex flex-col items-center">
            <div className="w-48 h-48 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-6xl text-gray-400">
                {profile.name?.charAt(0)?.toUpperCase() || '?'}
              </span>
            </div>
          </div>
      </div>

      {/* ✅ UPDATED: Platform-aware External Profile Links */}
      <div className="border rounded-lg p-6 bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">External Profiles</h3>
          {/* Platform Badges */}
          <div className="flex gap-2">
            {hasLinkedIn && (
              <Badge variant="default" className="text-xs">LinkedIn</Badge>
            )}
            {hasXing && (
              <Badge variant="secondary" className="text-xs">XING</Badge>
            )}
          </div>
        </div>
        
        <div className="space-y-3">
          {/* ✅ LinkedIn Button */}
          {hasLinkedIn && linkedinUrl ? (
            <Button
              onClick={() => handleOpenProfile(linkedinUrl, 'linkedin')}
              className="w-full justify-start"
              variant="outline"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              <span className="flex-1 text-left truncate">{linkedinUrl}</span>
              <Badge variant="default" className="ml-2 text-xs shrink-0">LinkedIn</Badge>
            </Button>
          ) : hasLinkedIn ? (
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">
                LinkedIn ID: <code className="bg-gray-200 px-1 rounded">{profile.linkedinId}</code>
              </span>
              <Badge variant="outline" className="text-xs">No URL</Badge>
            </div>
          ) : null}

          {/* ✅ NEU: XING Button */}
          {hasXing && xingUrl ? (
            <Button
              onClick={() => handleOpenProfile(xingUrl, 'xing')}
              className="w-full justify-start"
              variant="outline"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              <span className="flex-1 text-left truncate">{xingUrl}</span>
              <Badge variant="secondary" className="ml-2 text-xs shrink-0">XING</Badge>
            </Button>
          ) : hasXing ? (
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">
                XING ID: <code className="bg-gray-200 px-1 rounded">{(profile as any).xingId}</code>
              </span>
              <Badge variant="outline" className="text-xs">No URL</Badge>
            </div>
          ) : null}

          {/* No profiles available */}
          {!hasLinkedIn && !hasXing && (
            <p className="text-sm text-gray-400 italic text-center py-4">
              No external profile URLs available
            </p>
          )}
        </div>
      </div>

      {/* Parsed Raw Data */}
      <div className="border rounded-lg p-6 bg-white">
        <h3 className="text-lg font-semibold mb-4">Profile Raw Data</h3>
        <ParsedRawData rawData={profile.rawData} />
      </div>
    </div>
  );
}